import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { Calendar, Clock, User, BookOpen, Loader2, Play } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [activeTab, setActiveTab] = useState('articles');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [articlesRes, podcastsRes] = await Promise.all([
        axios.get(`${API}/articles`),
        axios.get(`${API}/podcasts`)
      ]);
      setArticles(articlesRes.data);
      setPodcasts(podcastsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-700" />
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
            Insights & Analysis
          </h1>
          <p className="text-lg text-slate-600">
            Expert articles, analysis, and the "Your House" podcast series on citizen engagement
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="grid w-full max-w-md grid-cols-2 h-auto gap-2 bg-transparent p-0">
            <TabsTrigger
              value="articles"
              data-testid="articles-tab"
              className="rounded-lg border-2 border-slate-200 data-[state=active]:border-primary-700 data-[state=active]:bg-primary-50 data-[state=active]:text-primary-900 py-3"
            >
              <div className="flex items-center space-x-2">
                <BookOpen className="h-4 w-4" />
                <span className="font-medium">Articles & Blogs</span>
              </div>
            </TabsTrigger>
            <TabsTrigger
              value="podcasts"
              data-testid="podcasts-tab"
              className="rounded-lg border-2 border-slate-200 data-[state=active]:border-primary-700 data-[state=active]:bg-primary-50 data-[state=active]:text-primary-900 py-3"
            >
              <div className="flex items-center space-x-2">
                <Play className="h-4 w-4" />
                <span className="font-medium">Your House Podcast</span>
              </div>
            </TabsTrigger>
          </TabsList>

          {/* Articles Tab */}
          <TabsContent value="articles" className="mt-8">
            {selectedArticle ? (
              <div>
                <button
                  onClick={() => setSelectedArticle(null)}
                  className="mb-6 text-primary-700 hover:text-primary-900 font-medium flex items-center space-x-2"
                  data-testid="back-to-articles"
                >
                  <span>←</span>
                  <span>Back to all articles</span>
                </button>

                <article className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  {selectedArticle.image_url && (
                    <div className="w-full h-96 overflow-hidden">
                      <img
                        src={selectedArticle.image_url}
                        alt={selectedArticle.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-8 md:p-12">
                    <Badge className="mb-4 bg-primary-50 text-primary-700 hover:bg-primary-100">
                      {selectedArticle.category}
                    </Badge>
                    <h1 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
                      {selectedArticle.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-8 pb-8 border-b border-slate-200">
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4" />
                        <span>{selectedArticle.author}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(selectedArticle.date)}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4" />
                        <span>{selectedArticle.read_time} min read</span>
                      </div>
                    </div>
                    <div className="prose prose-lg max-w-none">
                      <p className="text-xl text-slate-700 leading-relaxed mb-6">
                        {selectedArticle.excerpt}
                      </p>
                      <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                        {selectedArticle.content}
                      </p>
                    </div>
                  </div>
                </article>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article, index) => (
                  <Card
                    key={article.id}
                    className="shadow-sm hover:shadow-md transition-all cursor-pointer overflow-hidden group"
                    onClick={() => setSelectedArticle(article)}
                    data-testid={`article-card-${index}`}
                  >
                    {article.image_url && (
                      <div className="w-full h-48 overflow-hidden">
                        <img
                          src={article.image_url}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <CardContent className="p-6">
                      <Badge className="mb-3 bg-primary-50 text-primary-700 text-xs">
                        {article.category}
                      </Badge>
                      <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 group-hover:text-primary-700 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <div className="flex items-center space-x-1">
                          <User className="h-3 w-3" />
                          <span>{article.author}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock className="h-3 w-3" />
                          <span>{article.read_time} min</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Podcasts Tab */}
          <TabsContent value="podcasts" className="mt-8">
            <div className="mb-8 bg-primary-50 rounded-2xl p-8 border border-primary-200">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-primary-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Play className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-2">
                    Your House: A Radio Drama Series
                  </h2>
                  <p className="text-slate-700 leading-relaxed">
                    An engaging radio drama that brings citizen engagement to life through stories 
                    and practical examples. Each episode focuses on different aspects of engaging with 
                    your representatives and the legislative process.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {podcasts.map((podcast, index) => (
                <Card
                  key={podcast.id}
                  className="shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`podcast-card-${index}`}
                >
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Video Player */}
                      <div className="lg:col-span-2">
                        <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden">
                          <iframe
                            width="100%"
                            height="100%"
                            src={podcast.youtube_url}
                            title={podcast.title}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full"
                          ></iframe>
                        </div>
                      </div>

                      {/* Podcast Info */}
                      <div className="flex flex-col justify-between">
                        <div>
                          <div className="flex items-center space-x-2 mb-3">
                            <Badge className="bg-accent text-white">
                              Episode {podcast.episode}
                            </Badge>
                            <span className="text-sm text-slate-600">{podcast.duration}</span>
                          </div>
                          <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                            {podcast.title}
                          </h3>
                          <p className="text-slate-600 text-sm leading-relaxed mb-4">
                            {podcast.description}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-slate-500">
                          <Calendar className="h-3 w-3" />
                          <span>{formatDate(podcast.date)}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-br from-primary-700 to-emerald-800 rounded-2xl p-8 md:p-12 text-white">
          <div className="max-w-3xl">
            <h3 className="font-heading text-2xl md:text-3xl font-semibold mb-4">
              Share Your Engagement Story
            </h3>
            <p className="text-emerald-100 leading-relaxed mb-6">
              Have you successfully engaged with your representative? Your story could inspire others. 
              We welcome guest contributions and community success stories for our blog.
            </p>
            <a
              href="mailto:contact@globalrights.org?subject=Article Submission"
              className="inline-block bg-white text-primary-900 hover:bg-emerald-50 rounded-full px-8 py-3 font-medium transition-colors"
            >
              Submit Your Story
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Articles;
